/**
 * Offline Sync Queue Service
 *
 * Queues bookmark save requests in IndexedDB when the device is offline
 * (or when a save request fails due to network error). Replays them
 * automatically when connectivity returns — either through the Background
 * Sync API or a manual online-event fallback.
 *
 * Storage: IndexedDB database `linkflare_sync_queue`, object store `pending_saves`.
 */

import { httpStatusFromError } from '../utils/requestRetry.js'
import { isForeignError } from '../utils/apiErrorFacts.js'

const DB_NAME = 'linkflare_sync_queue'
const DB_VERSION = 1
const STORE_NAME = 'pending_saves'

/**
 * How many times a replay may come back with a VERDICT ON THE PAYLOAD before
 * flush() stops offering that item.
 *
 * The item is never deleted: the card the user is looking at is backed by this
 * payload and nothing else (see utils/pendingBookmark.js), so dropping it would
 * turn the card into a ghost that vanishes on reload — the exact failure
 * queueLostSave exists to prevent. Exhausted items just stop being retried, and
 * `removeByUrl` still clears them when the user deletes the card.
 */
export const MAX_REPLAY_ATTEMPTS = 5

/**
 * Did the server evaluate THIS payload and reject it?
 *
 * Deliberately narrower than "a status came back". The cap exists for a poison
 * pill — a payload the server will reject identically forever — so only a
 * failure that is ABOUT the payload may count against it:
 *
 *   - 5xx is the server breaking while handling the write, not a verdict on the
 *     request (`utils/linkSaveOutcome.js` splits on exactly this axis, and the
 *     two must not disagree). A bare 500 window during a deploy would otherwise
 *     burn one attempt on EVERY queued item per flush trigger, and five
 *     triggers — a startup, an `online` event, the 30 s queueLostSave timer —
 *     would strand the whole queue permanently, healthy backend and all.
 *   - 401 is the token, and 408/429 are our request rate. Shared conditions,
 *     same reasoning.
 *   - A 4xx tagged as a foreign response came from an intermediary rather than
 *     our API, so it cannot be a verdict on the payload either.
 *   - No readable status at all (a transport failure, a TimeoutError, a bug in
 *     saveFn) says nothing about anything. Never counted.
 *
 * Everything it rejects breaks the loop instead, burning no attempt — the
 * network or the backend is simply not ready, and the next trigger tries again.
 */
function isVerdictOnPayload(error) {
  if (isForeignError(error)) return false
  const status = httpStatusFromError(error)
  if (status === null || status < 400 || status >= 500) return false
  return status !== 401 && status !== 408 && status !== 429
}

class OfflineSyncQueue {
  constructor() {
    this.db = null
    this._onSyncCallbacks = []
    this._listeningOnline = false
  }

  // ── IndexedDB bootstrap ──────────────────────────────────────────────

  async _ensureDb() {
    if (this.db) return this.db
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onerror = () => reject(req.error)
      req.onsuccess = () => { this.db = req.result; resolve(this.db) }
      req.onupgradeneeded = (e) => {
        const db = e.target.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
        }
      }
    })
  }

  // ── Queue operations ─────────────────────────────────────────────────

  /**
   * Add a bookmark-save payload to the offline queue.
   * @param {object} linkData — The same object passed to ApiService.saveLink()
   * @param {boolean} runRules — Whether to run automation rules on replay
   */
  async enqueue(linkData, runRules = true) {
    const db = await this._ensureDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const req = store.add({ linkData, runRules, queuedAt: Date.now() })
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Return all queued items (oldest first).
   * @returns {Promise<Array<{id: number, linkData: object, runRules: boolean, queuedAt: number}>>}
   */
  async getAll() {
    const db = await this._ensureDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => reject(req.error)
    })
  }

  /** Return the current row for an id, or undefined when it was deleted. */
  async _get(id) {
    const db = await this._ensureDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(id)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Persist the replay-attempt count for one item.
   *
   * Reads the row back inside the same transaction and writes only if it is
   * still there. A bare `put` would INSERT when the key is gone, and the key
   * can be gone: `flush()` works from a snapshot and holds it across a saveFn
   * that runs 14-22 s at the median, which is ample time for the user to delete
   * the card and `removeByUrl` to drop the payload. Re-inserting it would let
   * the next flush re-create the bookmark they just deleted — the resurrection
   * `removeByUrl` exists to prevent (lessons.md: deletion needs a tombstone,
   * never absence). Merging onto the row we just read, rather than onto the
   * stale snapshot, keeps any concurrent change to it as well.
   *
   * Best-effort: if this write fails the item simply keeps its old count and
   * gets offered again, which is the same behaviour as before the counter
   * existed. Never let it reject into flush()'s fire-and-forget callers.
   */
  async _recordAttempt(item, attempts) {
    try {
      const db = await this._ensureDb()
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        const read = store.get(item.id)
        read.onerror = () => reject(read.error)
        read.onsuccess = () => {
          const current = read.result
          if (!current) return resolve() // Deleted mid-flush — let it stay deleted.
          const write = store.put({ ...current, attempts })
          write.onsuccess = () => resolve()
          write.onerror = () => reject(write.error)
        }
      })
    } catch (err) {
      console.warn('Could not record replay attempt for item', item.id, err)
    }
  }

  /**
   * Remove a single item from the queue (after successful replay).
   */
  async remove(id) {
    const db = await this._ensureDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const req = tx.objectStore(STORE_NAME).delete(id)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Drop every parked save for a URL, and report how many were dropped.
   *
   * The queue is the only thing keeping an unsent bookmark alive, so it is also
   * the only place a delete can act on one. Without this, deleting a card whose
   * save is still parked removes the card and leaves the payload — and the next
   * flush (the 30 s timer `queueLostSave` schedules, the `online` listener, or
   * the App.vue startup flush) re-creates the bookmark the user deleted.
   * lessons.md: deletion needs a tombstone, never absence — the same shape as
   * the feed reader resurrecting deleted bookmarks.
   *
   * Keyed on the URL rather than the queue id because the caller has a card,
   * not a queue entry, and `enqueue` returns its id to nobody who keeps it. A
   * URL can legitimately appear more than once (the user retried), so remove
   * ALL matches — leaving one behind would resurrect the bookmark just the same.
   *
   * Reject-proof for the same reason `flush()` is: callers are delete paths that
   * must not break because IndexedDB is unavailable in this browser. A queue we
   * cannot read holds nothing that can be replayed either, so degrading to 0 is
   * accurate as well as safe.
   *
   * @param {string} url — `linkData.url` of the parked save(s) to cancel
   * @returns {Promise<number>} how many queued items were removed
   */
  async removeByUrl(url) {
    if (!url) return 0

    let items
    try {
      items = await this.getAll()
    } catch (err) {
      console.warn('Offline sync queue unavailable — cannot cancel parked save:', err)
      return 0
    }

    let removed = 0
    for (const item of items) {
      if (item?.linkData?.url !== url) continue
      try {
        await this.remove(item.id)
        removed++
      } catch (err) {
        console.warn('Could not cancel parked save', item.id, err)
      }
    }
    return removed
  }

  /**
   * Merge an edit into every parked save for a URL, and report how many changed.
   *
   * The edit-side twin of `removeByUrl`: a card whose save is parked is backed
   * by this payload, not by a row, so the payload is the only place an edit can
   * land. Sending it to `PUT /links` instead creates the bookmark by URL (the
   * temp id parses to null server-side), and the parked save then replays over
   * it and reverts the edit (see utils/pendingBookmark.js).
   *
   * The merged payload:
   *   - never carries an `id`. It is still a create.
   *   - replays with `runRules: false`. A manual edit is authoritative, and
   *     every edit path sends run_rules=false, or a "VIDEO → inbox" rule undoes
   *     an archive on replay.
   *   - starts its replay attempts from 0. It is a new payload, and an edit (a
   *     corrected URL) can cure the rejection that exhausted the old one.
   *
   * Each row is re-read and written in one transaction, so a row `removeByUrl`
   * or a successful replay dropped in the meantime stays dropped rather than
   * being re-inserted by `put` (lessons.md).
   *
   * Reject-proof like `removeByUrl`: when IndexedDB is unavailable nothing can
   * have been parked, so 0 is accurate.
   *
   * @param {string} url — `linkData.url` of the parked save(s) to edit
   * @param {object} changes — fields to merge into `linkData`
   * @returns {Promise<number>} how many queued items were updated
   */
  async amendByUrl(url, changes) {
    if (!url) return 0

    let items
    try {
      items = await this.getAll()
    } catch (err) {
      console.warn('Offline sync queue unavailable — cannot edit parked save:', err)
      return 0
    }

    let amended = 0
    for (const item of items) {
      if (item?.linkData?.url !== url) continue
      try {
        const db = await this._ensureDb()
        const written = await new Promise((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite')
          const store = tx.objectStore(STORE_NAME)
          const read = store.get(item.id)
          read.onerror = () => reject(read.error)
          read.onsuccess = () => {
            const current = read.result
            if (!current) return resolve(false) // Replayed or deleted meanwhile.
            const linkData = { ...current.linkData, ...changes }
            delete linkData.id
            const write = store.put({ ...current, linkData, runRules: false, attempts: 0 })
            write.onsuccess = () => resolve(true)
            write.onerror = () => reject(write.error)
          }
        })
        if (written) amended++
      } catch (err) {
        console.warn('Could not edit parked save', item.id, err)
      }
    }
    return amended
  }

  /**
   * Number of items waiting to sync.
   */
  async count() {
    const db = await this._ensureDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).count()
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  // ── Replay (flush) ──────────────────────────────────────────────────

  /**
   * Attempt to replay all queued saves.
   *
   * @param {function} saveFn — `(linkData, token, runRules) => Promise` — the
   *   actual API call (typically `ApiService.saveLink`).
   * @param {function} getToken — `() => Promise<string|null>` — to fetch a
   *   fresh auth token.
   * @returns {Promise<number>} — number of items successfully synced
   */
  async flush(saveFn, getToken) {
    // Reading the queue can reject when IndexedDB is unavailable — e.g.
    // "UnknownError: Internal error." on devices whose storage backend is
    // broken/quota-exhausted, or private-mode restrictions. The offline queue
    // is progressive enhancement and flush() is only ever called best-effort
    // (App.vue startup, the `online` listener), so degrade to a no-op instead
    // of leaking an unhandled promise rejection into those fire-and-forget
    // callers. See Sentry issue 130854759.
    let items
    try {
      items = await this.getAll()
    } catch (err) {
      console.warn('Offline sync queue unavailable — skipping flush:', err)
      return 0
    }
    if (items.length === 0) return 0

    // Exhausted items stay in the queue on purpose (see MAX_REPLAY_ATTEMPTS) but
    // are never offered again, so a queue made only of them is as much of a
    // no-op as an empty one — and must not cost a token round trip on every
    // startup and every `online` event for the life of the install.
    const replayable = items.filter(item => (item.attempts || 0) < MAX_REPLAY_ATTEMPTS)
    if (replayable.length === 0) return 0

    // getToken() can also reject (e.g. the auth provider's session refresh fails). flush() is only
    // ever called fire-and-forget (App.vue startup, the `online` listener), so guard this too —
    // otherwise a rejecting getToken escapes as the unhandled rejection this change set out to
    // eliminate (Sentry 130854759). Keeping flush() genuinely reject-proof protects every caller.
    let token
    try {
      token = await getToken()
    } catch (err) {
      console.warn('Offline sync: token fetch failed — skipping flush:', err)
      return 0
    }
    if (!token) return 0 // Still not authenticated — try later

    let synced = 0
    for (const snapshotItem of replayable) {
      // The initial list is only a candidate snapshot. An earlier save can be
      // in flight long enough for the user to delete a later queued card, so
      // read each row again immediately before offering it to saveFn. Using
      // the current row also preserves any concurrent update to its payload or
      // attempt count.
      let item
      try {
        item = await this._get(snapshotItem.id)
      } catch (err) {
        console.warn('Offline sync queue unavailable during flush:', err)
        break
      }
      if (!item || (item.attempts || 0) >= MAX_REPLAY_ATTEMPTS) continue

      try {
        await saveFn(item.linkData, token, item.runRules)
        await this.remove(item.id)
        synced++
      } catch (err) {
        // A verdict on THIS payload must not stop the queue: before the attempt
        // counter, one repeatably-rejected item sat at the head of the loop and
        // blocked every save behind it forever. That path became reachable when
        // a 500 on PUT /links started routing here instead of deleting the card
        // (see utils/linkSaveOutcome.js).
        if (isVerdictOnPayload(err)) {
          const attempts = (item.attempts || 0) + 1
          console.warn(`Sync replay rejected for item ${item.id} (attempt ${attempts}/${MAX_REPLAY_ATTEMPTS})`, err)
          await this._recordAttempt(item, attempts)
          continue
        }
        // Anything else — a transport failure, a timeout, a 5xx, an expired
        // token, an error we cannot read — says nothing about this item and
        // everything about the network or the backend. Stop, burn no attempt,
        // and let the next trigger try again.
        console.warn('Sync replay failed for item', item.id, err)
        break
      }
    }

    if (synced > 0) {
      this._notifySync(synced)
    }
    return synced
  }

  // ── Online-event fallback ────────────────────────────────────────────

  /**
   * Start listening for the browser `online` event so we can replay
   * queued saves even when the Background Sync API isn't available.
   *
   * Call this once at app startup and pass references to the API save
   * function and token getter.
   */
  startOnlineListener(saveFn, getToken) {
    if (this._listeningOnline) return
    this._listeningOnline = true

    // flush() is best-effort and swallows storage errors internally, and it
    // already early-returns on an empty queue — so call it directly. A plain
    // (non-async) handler avoids the previous leak where an `await` inside an
    // async event handler could escape as an unhandled rejection when storage
    // is broken (Sentry 130854759).
    window.addEventListener('online', () => {
      this.flush(saveFn, getToken)
    })
  }

  // ── Callback registry (for toast notifications) ──────────────────────

  /**
   * Register a callback to be invoked when queued items are synced.
   * @param {function} cb — `(syncedCount: number) => void`
   */
  onSync(cb) {
    this._onSyncCallbacks.push(cb)
  }

  /**
   * Remove all registered onSync callbacks.
   * Call before re-registering to prevent duplicate notifications.
   */
  clearCallbacks() {
    this._onSyncCallbacks = []
  }

  _notifySync(count) {
    for (const cb of this._onSyncCallbacks) {
      try { cb(count) } catch (e) { console.warn('Sync callback error', e) }
    }
    // Dispatch a window event so other components (e.g. Bookmarks) can react
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('linkflare-sync-complete', { detail: { count } }))
    }
  }
}

export default new OfflineSyncQueue()
